import { useEffect, useRef } from "react";
import useAppStore from "../stores/useAppStore";
import { generateSessionForClient } from "../services/generateSession";
import { track, EVENTS } from "../services/analytics";

const DEBOUNCE_MS = 3 * 60 * 1000;

/**
 * Auto-regen: debounced regeneration of stale AI sessions.
 * Triggers on:
 *  - coachProfile change (style de coaching)
 *  - equipment change (matériel dispo)
 *  - regenTrigger bump (notes coach, infos client)
 * Flags all eligible clients as stale, then regenerates after 3 min of inactivity.
 */
export function useAutoRegen() {
  const coachProfile = useAppStore((s) => s.coachProfile);
  const equipment = useAppStore((s) => s.equipment);
  const regenTrigger = useAppStore((s) => s.regenTrigger);
  const onboarded = useAppStore((s) => s.onboarded);
  const timerRef = useRef(null);
  const initialRef = useRef(true);

  useEffect(() => {
    if (!onboarded) return;
    if (initialRef.current) {
      initialRef.current = false;
      return;
    }

    // @deprecated Workouts 1b — ce hook régénère le chemin legacy JSON (nextAiSession/aiSessionStale).
    // À migrer vers persistAiSessionForClient (vraie instance jouable) — Workouts 1c.
    const state = useAppStore.getState();
    const hasStale = state.clients.some((c) => c.nextAiSession && c.status !== "archived");
    if (hasStale) {
      state.setClients((prev) =>
        prev.map((c) =>
          c.nextAiSession && c.status !== "archived" ? { ...c, aiSessionStale: true } : c
        )
      );
    }

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const fresh = useAppStore.getState();
      const stale = fresh.clients.filter((c) => c.aiSessionStale && c.status !== "archived");
      for (const cl of stale) {
        try {
          const latest = useAppStore.getState();
          const client = latest.clients.find((c) => c.id === cl.id);
          if (!client || !client.aiSessionStale) continue;
          const session = await generateSessionForClient(client, latest.clients, latest.equipment, latest.coachProfile);
          if (session) {
            track(EVENTS.AI_SESSION_GENERATED, { trigger: "debounce", clientId: cl.id });
            latest.setClients((p) =>
              p.map((c) => c.id === cl.id ? { ...c, nextAiSession: session, aiSessionStale: false } : c)
            );
          }
        } catch { /* silent */ }
      }
    }, DEBOUNCE_MS);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [coachProfile, equipment, regenTrigger, onboarded]);
}
